import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProductsService } from '../products.service';
import { environment } from 'src/environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { toastMessages } from 'src/app/components/constants';

@Component({
  selector: 'app-re-upload-files',
  templateUrl: './re-upload-files.component.html',
  styleUrls: ['./re-upload-files.component.scss']
})
export class ReUploadFilesComponent implements OnInit {
  orderId: number;
  orderData: any = null;
  isLoadingOrder = false;
  orderNotFound = false;

  fileTypes = [
    { name: 'Home Inspection Report', key: 'homeInsReport', type: 'HomeInspection', urlKey: 'homeinsurl' },
    { name: 'DDRA Report', key: 'ddraReport', type: 'DDRA', urlKey: 'ddraurl' },
    { name: 'WDO Report', key: 'wdoReport', type: 'WDO', urlKey: 'wdourl' },
    { name: 'Structural Engineering Report', key: 'structEngReport', type: 'StructuralEng', urlKey: 'structengurl' },
    { name: 'Well Report', key: 'wellReport', type: 'WellReport', urlKey: 'wellurl' }
  ];

  // New property to control visible file types
  visibleFileTypes: any[] = [];

  uploadResults: { [key: string]: { success: boolean, message: string } } = {};
  existingFiles: { [key: string]: string } = {}; // Store existing file URLs

  uploadForm: FormGroup;
  isLoading = false;
  filesToUpload: Map<string, File> = new Map();

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private productsService: ProductsService,
    private snackBar: MatSnackBar
  ) {
    this.uploadForm = this.fb.group({
      homeInsReport: [''],
      ddraReport: [''],
      wdoReport: [''],
      structEngReport: [''],
      wellReport: ['']
    });
  }

  ngOnInit(): void {
    // Get orderId from route params
    this.orderId = +this.route.snapshot.params['orderId'];

    if (this.orderId && !isNaN(this.orderId)) {
      this.fetchOrderData();
    } else {
      this.showToastMessage('Invalid Order ID provided', toastMessages.failed);
      this.orderNotFound = true;
    }
  }

  async fetchOrderData() {
    this.isLoadingOrder = true;
    this.orderNotFound = false;

    try {
      const payload = { input: this.orderId };
      const response: any = await this.productsService.listSingleOrderOfMarkets(payload);

      if (response && response.data && response.data.allOrders && response.data.allOrders.nodes && response.data.allOrders.nodes.length > 0) {
        this.orderData = response.data.allOrders.nodes[0];
        console.log('Order data fetched successfully:', this.orderData);

        // Parse order content if it exists and is in JSON format
        if (this.orderData.orderContent) {
          try {
            const parsedContent = JSON.parse(this.orderData.orderContent);
            this.orderData.parsedContent = parsedContent;
            console.log('Parsed order content:', parsedContent);
          } catch (parseError) {
            console.warn('Could not parse order content as JSON:', parseError);
          }
        }

        // Set visible file types based on productId
        this.setVisibleFileTypes();

        // Prefill existing files
        this.prefillExistingFiles();

        this.showToastMessage('Order details loaded successfully', toastMessages.success);
      } else {
        this.orderNotFound = true;
        this.showToastMessage('Order not found with the provided ID', toastMessages.warning);
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
      this.orderNotFound = true;
      this.showToastMessage('Failed to load order details. Please try again.', toastMessages.failed);
    } finally {
      this.isLoadingOrder = false;
    }
  }

  private setVisibleFileTypes() {
    // Check if productId is 3
    if (this.orderData && this.orderData.productId === 3) {
      console.log('ProductId is 3, showing only Home Inspection Report');
      // Show only Home Inspection Report
      this.visibleFileTypes = this.fileTypes.filter(fileType => fileType.key === 'homeInsReport');
    } else {
      console.log('ProductId is not 3, showing all file types. ProductId:', this.orderData?.productId);
      // Show all file types
      this.visibleFileTypes = [...this.fileTypes];
    }
  }

  private prefillExistingFiles() {
    if (!this.orderData) return;

    // Reset existing files
    this.existingFiles = {};

    // Check each visible file type for existing URLs (only process visible ones)
    this.visibleFileTypes.forEach(fileType => {
      const existingUrl = this.orderData[fileType.urlKey];

      if (existingUrl && existingUrl.trim() !== '') {
        this.existingFiles[fileType.key] = existingUrl;

        // Extract filename from URL for display
        const fileName = this.extractFileNameFromUrl(existingUrl);
        this.uploadForm.get(fileType.key)?.setValue(`Existing: ${fileName}`);
      } else {
        // Clear the form control if no existing file
        this.uploadForm.get(fileType.key)?.setValue('');
      }
    });

    console.log('Existing files:', this.existingFiles);
  }

  private extractFileNameFromUrl(url: string): string {
    try {
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      return decodeURIComponent(fileName) || 'Unknown file';
    } catch (error) {
      return 'Unknown file';
    }
  }

  // Public method for template access
  extractFileNameFromUrl_Public(url: string): string {
    return this.extractFileNameFromUrl(url);
  }

  async submitUploads() {
    if (this.filesToUpload.size === 0) {
      this.showToastMessage('Please select at least one file to upload', toastMessages.warning);
      return;
    }

    if (!this.orderData) {
      this.showToastMessage('Order data not available. Please refresh and try again.', toastMessages.failed);
      return;
    }

    this.isLoading = true;
    this.uploadResults = {};
    let allSuccess = true;
    let successCount = 0;
    let failedCount = 0;
    const successfulUploadUrls: { [key: string]: string } = {};

    // Show initial progress message
    this.showToastMessage(`Starting upload of ${this.filesToUpload.size} file(s)...`, toastMessages.info);

    // Create a copy of files to upload before processing
    const filesToProcess = new Map(this.filesToUpload);

    for (const [key, file] of filesToProcess) {
      const fileTypeConfig = this.fileTypes.find(ft => ft.key === key);

      if (file && fileTypeConfig) {
        try {
          console.log(`Uploading ${fileTypeConfig.name} for order ${this.orderId}...`);

          const result = await this.productsService.reportUpload(file, this.orderId, fileTypeConfig.type);

          if (this.isUploadSuccessful(result)) {
            this.uploadResults[key] = {
              success: true,
              message: 'Upload successful!'
            };

            // Store the new URL for the successful upload
            const newUrl = `${environment.apiHost}reports/${this.orderId}/${fileTypeConfig.type}/${encodeURIComponent(file.name)}`;
            successfulUploadUrls[this.getUrlKey(fileTypeConfig.key)] = newUrl;

            // Remove from filesToUpload only if it's the same file (in case user changed it during upload)
            if (this.filesToUpload.get(key) === file) {
              this.filesToUpload.delete(key);
            }

            successCount++;
            console.log(`${fileTypeConfig.name} uploaded successfully`);
          } else {
            this.uploadResults[key] = {
              success: false,
              message: 'Upload failed - Please try again'
            };
            failedCount++;
            allSuccess = false;
            console.error(`${fileTypeConfig.name} upload failed:`, result);
          }
        } catch (error) {
          console.error(`Error uploading ${fileTypeConfig.name}:`, error);
          this.uploadResults[key] = {
            success: false,
            message: error?.message || 'Error during upload - Please try again'
          };
          failedCount++;
          allSuccess = false;
        }
      }
    }

    this.isLoading = false;

    // Show summary message
    if (allSuccess && successCount > 0) {
      this.showToastMessage(`All ${successCount} file(s) uploaded successfully!`, toastMessages.success);

      // Update the order with new URLs
      await this.updateOrderWithNewFiles(successfulUploadUrls);

      // Clear form for successful uploads
      this.clearSuccessfullyUploadedFiles(filesToProcess);

      // Refresh order data to show updated files
      this.refreshOrderData();
    } else if (successCount > 0 && failedCount > 0) {
      this.showToastMessage(`${successCount} file(s) uploaded successfully, ${failedCount} failed. Check individual results below.`, toastMessages.warning);

      // Update order with successful uploads only
      if (Object.keys(successfulUploadUrls).length > 0) {
        await this.updateOrderWithNewFiles(successfulUploadUrls);
      }
    } else if (failedCount > 0) {
      this.showToastMessage(`All ${failedCount} file(s) failed to upload. Please check the errors and try again.`, toastMessages.failed);
    }
  }

  private clearSuccessfullyUploadedFiles(filesToProcess: Map<string, File>) {
    // Clear only the successfully uploaded files from the form
    filesToProcess.forEach((file, key) => {
      if (this.uploadResults[key]?.success) {
        this.uploadForm.get(key)?.setValue('');
        const fileInput = document.querySelector(`input[formControlName="${key}"]`) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      }
    });
  }

  private getUrlKey(formKey: string): string {
    const mapping = {
      'homeInsReport': 'homeInsUrl',
      'ddraReport': 'ddraUrl',
      'wdoReport': 'wdoUrl',
      'structEngReport': 'structEngUrl',
      'wellReport': 'wellUrl'
    };
    return mapping[formKey] || formKey;
  }

  private async updateOrderWithNewFiles(successfulUploadUrls: { [key: string]: string }) {
    try {
      // Create update payload with merged URLs (new + existing)
      const updateProductForms = {
        id: this.orderId,
        homeInsUrl: successfulUploadUrls.homeInsUrl || this.orderData.homeinsurl,
        ddraUrl: successfulUploadUrls.ddraUrl || this.orderData.ddraurl,
        wdoUrl: successfulUploadUrls.wdoUrl || this.orderData.wdourl,
        structEngUrl: successfulUploadUrls.structEngUrl || this.orderData.structengurl,
        wellUrl: successfulUploadUrls.wellUrl || this.orderData.wellurl,
      };

      // Parse the existing order content
      let orderContent = this.orderData.parsedContent;
      if (!orderContent && this.orderData.orderContent) {
        try {
          orderContent = JSON.parse(this.orderData.orderContent);
        } catch (e) {
          console.error('Error parsing order content:', e);
          orderContent = {};
        }
      }

      // Update the URLs in meta_data (top level)
      if (orderContent.meta_data && Array.isArray(orderContent.meta_data)) {
        orderContent.meta_data = orderContent.meta_data.map((meta: any) => {
          // Handle both variations of home inspection
          if ((meta.key === 'homeInspection' || meta.key === 'PDF - Home Inspection') && updateProductForms.homeInsUrl) {
            return { ...meta, value: updateProductForms.homeInsUrl };
          }
          if ((meta.key === 'ddra' || meta.key === 'PDF - DDRA') && updateProductForms.ddraUrl) {
            return { ...meta, value: updateProductForms.ddraUrl };
          }
          if (meta.key === 'wdoReport' && updateProductForms.wdoUrl) {
            return { ...meta, value: updateProductForms.wdoUrl };
          }
          if (meta.key === 'structEngReport' && updateProductForms.structEngUrl) {
            return { ...meta, value: updateProductForms.structEngUrl };
          }
          if (meta.key === 'wellReport' && updateProductForms.wellUrl) {
            return { ...meta, value: updateProductForms.wellUrl };
          }
          return meta;
        });
      }

      // Update the URLs in line_items meta_data
      if (orderContent.line_items && Array.isArray(orderContent.line_items)) {
        orderContent.line_items = orderContent.line_items.map((item: any) => {
          if (item.meta_data && Array.isArray(item.meta_data)) {
            item.meta_data = item.meta_data.map((meta: any) => {
              // Handle both variations of home inspection in line items
              if ((meta.key === 'PDF - Home Inspection' || meta.key === 'PDF - Home Inspection ') && updateProductForms.homeInsUrl) {
                return { ...meta, value: updateProductForms.homeInsUrl };
              }
              if ((meta.key === 'homeInspection') && updateProductForms.homeInsUrl) {
                return { ...meta, value: updateProductForms.homeInsUrl };
              }
              if ((meta.key === 'PDF - DDRA' || meta.key === 'ddra') && updateProductForms.ddraUrl) {
                return { ...meta, value: updateProductForms.ddraUrl };
              }
              if (meta.key === 'wdoReport' && updateProductForms.wdoUrl) {
                return { ...meta, value: updateProductForms.wdoUrl };
              }
              if (meta.key === 'structEngReport' && updateProductForms.structEngUrl) {
                return { ...meta, value: updateProductForms.structEngUrl };
              }
              if (meta.key === 'wellReport' && updateProductForms.wellUrl) {
                return { ...meta, value: updateProductForms.wellUrl };
              }
              return meta;
            });
          }
          return item;
        });
      }

      // Prepare the complete update payload
      const updatePayload = {
        orderContent: JSON.stringify(orderContent),
        homeinsurl: updateProductForms.homeInsUrl,
        ddraurl: updateProductForms.ddraUrl,
        wdourl: updateProductForms.wdoUrl,
        structengurl: updateProductForms.structEngUrl,
        wellurl: updateProductForms.wellUrl,
        // Keep other existing fields that shouldn't change
        notes: this.orderData.notes,
        statusId: this.orderData.statusId
      };

      console.log('Final update payload:', updatePayload);

      // Update the order with both URLs and orderContent
      await this.productsService.updateOrder(updatePayload, this.orderId);
      console.log('Order updated successfully with new file URLs and orderContent');

      this.showToastMessage('Order updated successfully with new files', toastMessages.success);

    } catch (error) {
      console.error('Error updating order with new file URLs:', error);
      this.showToastMessage('Files uploaded but order update failed. Please contact support.', toastMessages.warning);

      // Fallback: Update only the URLs without orderContent
      try {
        const fallbackPayload = {
          homeinsurl: successfulUploadUrls.homeInsUrl || this.orderData.homeinsurl,
          ddraurl: successfulUploadUrls.ddraUrl || this.orderData.ddraurl,
          wdourl: successfulUploadUrls.wdoUrl || this.orderData.wdourl,
          structengurl: successfulUploadUrls.structEngUrl || this.orderData.structengurl,
          wellurl: successfulUploadUrls.wellUrl || this.orderData.wellurl
        };

        await this.productsService.updateOrder(fallbackPayload, this.orderId);
        console.log('Order updated with URLs only (orderContent update failed)');
        this.showToastMessage('Files updated successfully (partial update)', toastMessages.warning);
      } catch (fallbackError) {
        console.error('Fallback update also failed:', fallbackError);
        this.showToastMessage('Files uploaded but order update completely failed. Please contact support.', toastMessages.failed);
      }
    }
  }

  private extractMetaValue(parsedContent: any, key: string): any {
    try {
      const metaItem = parsedContent.meta_data?.find((meta: any) => meta.key === key);
      return metaItem?.value || null;
    } catch (error) {
      console.warn(`Error extracting meta value for key ${key}:`, error);
      return null;
    }
  }

  private isUploadSuccessful(result: any): boolean {
    try {
      // Enhanced success check logic
      if (!result) {
        return false;
      }

      // Check for explicit success indicators
      if (result.status === 'completed' || result.status === 'success') {
        return true;
      }

      // Check for failure indicators
      if (result.status === 'failed' || result.error || result.failed) {
        return false;
      }

      // Check for HTTP status codes
      if (result.statusCode && result.statusCode >= 200 && result.statusCode < 300) {
        return true;
      }

      // Check for success boolean
      if (typeof result.success === 'boolean') {
        return result.success;
      }

      // If result exists but doesn't have clear status, consider it successful
      return true;
    } catch (error) {
      console.error('Error checking upload result:', error);
      return false;
    }
  }

  private showToastMessage(message: string, toastType: string) {
    this.snackBar.open(message, 'OK', {
      verticalPosition: 'top',
      duration: 4000,
      panelClass: [toastType],
    });
  }

  // Helper methods for template
  getUploadResultClass(key: string): string {
    const result = this.uploadResults[key];
    if (!result) return '';
    return result.success ? 'success' : 'error';
  }

  getUploadResultIcon(key: string): string {
    const result = this.uploadResults[key];
    if (!result) return '';
    return result.success ? '✅' : '❌';
  }

  hasAnyUploadResults(): boolean {
    return Object.keys(this.uploadResults).length > 0;
  }

  hasExistingFile(key: string): boolean {
    const existingUrl = this.existingFiles[key];
    const hasExisting = !!existingUrl && existingUrl.trim() !== '';
    return hasExisting;
  }

  getExistingFileUrl(key: string): string {
    return this.existingFiles[key] || '';
  }

  downloadExistingFile(key: string) {
    const url = this.existingFiles[key];
    if (url) {
      window.open(url, '_blank');
    }
  }

  // Method to refresh order data if needed
  refreshOrderData() {
    this.fetchOrderData();
  }

  validateFileSize(file: File): boolean {
    const maxSizeInBytes = 100 * 1024 * 1024; //100 MB in bytes
    return file.size <= maxSizeInBytes;
  }

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  triggerFileInput(fileInputElement: HTMLInputElement) {
    console.log('Trigger file input called, disabled?', fileInputElement.disabled);

    if (fileInputElement.disabled) {
      return;
    }

    fileInputElement.click();
  }

  getFileDisplayName(fileTypeKey: string): string {
    // Check if there's a file selected for upload first
    const selectedFile = this.filesToUpload.get(fileTypeKey);
    if (selectedFile) {
      return selectedFile.name;
    }

    // Check if there's an existing file and no new file selected
    if (this.hasExistingFile(fileTypeKey)) {
      const existingUrl = this.getExistingFileUrl(fileTypeKey);
      const fileName = this.extractFileNameFromUrl(existingUrl);
      return `Existing: ${fileName}`;
    }

    // Default message
    return 'No file chosen';
  }

  onFileSelected(event: any, fileTypeKey: string) {
    const file = event.target.files[0];

    console.log('File selected event triggered for:', fileTypeKey, 'File:', file);

    if (file) {
      // Check file size
      if (!this.validateFileSize(file)) {
        const fileSize = this.formatFileSize(file.size);
        this.uploadResults[fileTypeKey] = {
          success: false,
          message: `File size (${fileSize}) exceeds the maximum allowed size of 100 MB. Please select a smaller file.`
        };

        // Clear everything
        this.uploadForm.get(fileTypeKey)?.setValue('');
        event.target.value = '';
        this.filesToUpload.delete(fileTypeKey);
        return;
      }

      // Clear any previous errors
      if (this.uploadResults[fileTypeKey]) {
        delete this.uploadResults[fileTypeKey];
      }

      // Add to files to upload MAP (this is crucial!)
      this.filesToUpload.set(fileTypeKey, file);

      // Update form control with file name for display
      this.uploadForm.get(fileTypeKey)?.setValue(file.name);

      console.log('File added to upload map:', fileTypeKey, 'Total files:', this.filesToUpload.size);
      console.log('Current filesToUpload:', Array.from(this.filesToUpload.keys()));

    } else {
      // Clear if no file
      this.uploadForm.get(fileTypeKey)?.setValue('');
      this.filesToUpload.delete(fileTypeKey);
      console.log('File cleared for:', fileTypeKey, 'Total files:', this.filesToUpload.size);
    }
  }

  clearAllUploads() {
    console.log('Clearing all uploads...');

    // Clear the filesToUpload map completely
    this.filesToUpload.clear();

    // Clear upload results
    this.uploadResults = {};

    // Reset form controls and file inputs for visible file types only
    this.visibleFileTypes.forEach(fileType => {
      // Clear form control
      this.uploadForm.get(fileType.key)?.setValue('');

      // Clear the actual file input element
      const fileInput = document.querySelector(`input[formControlName="${fileType.key}"]`) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    });

    console.log('All uploads cleared. Files to upload:', this.filesToUpload.size);
  }
}